import { Injectable } from '@angular/core';
import { MatDateFormats, NativeDateAdapter } from '@angular/material/core';

/** App-wide Material date adapter. Display is fixed at `MM-DD-YYYY`; parsing
 * accepts any of `MM-DD-YYYY`, `M/D/YY`, `M/D/YYYY`, `YYYY-MM-DD`, or
 * `YYYY/MM/DD` (separators interchangeable: `-`, `/`, `.`, whitespace). */
@Injectable()
export class MmDdYyyyDateAdapter extends NativeDateAdapter {
  override parse(value: unknown): Date | null {
    if (value == null || value === '') {
      return null;
    }
    if (value instanceof Date) {
      return isNaN(value.getTime()) ? null : value;
    }
    if (typeof value !== 'string') {
      return super.parse(value);
    }
    const trimmed = value.trim();
    if (!trimmed) {
      return null;
    }

    const rawParts = trimmed.split(/[-/.\s]+/);
    if (rawParts.length !== 3) {
      return super.parse(value);
    }
    const parts = rawParts.map((p) => parseInt(p, 10));
    if (parts.some(isNaN)) {
      return super.parse(value);
    }

    let year: number;
    let month: number;
    let day: number;
    if (rawParts[0].length === 4) {
      [year, month, day] = parts;
    } else {
      [month, day, year] = parts;
      // 2-digit year: 00–49 → 2000s, 50–99 → 1900s. Matches the heuristic
      // most US-style date pickers and Excel use.
      if (rawParts[2].length <= 2) {
        year += year < 50 ? 2000 : 1900;
      }
    }

    if (month < 1 || month > 12 || day < 1 || day > 31) {
      return null;
    }
    const d = new Date(year, month - 1, day);
    if (d.getFullYear() !== year || d.getMonth() !== month - 1 || d.getDate() !== day) {
      return null;
    }
    return d;
  }

  override format(date: Date, _displayFormat: unknown): string {
    if (!date || isNaN(date.getTime())) {
      return '';
    }
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${m}-${d}-${date.getFullYear()}`;
  }
}

export const MM_DD_YYYY_DATE_FORMATS: MatDateFormats = {
  parse: {
    dateInput: 'MM-DD-YYYY',
  },
  display: {
    dateInput: 'MM-DD-YYYY',
    monthYearLabel: { year: 'numeric', month: 'short' },
    dateA11yLabel: { year: 'numeric', month: 'long', day: 'numeric' },
    monthYearA11yLabel: { year: 'numeric', month: 'long' },
  },
};
